import React, { useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import {
  PALETTE,
  Card,
  CardLabel,
  CardRow,
  CardValue,
  ErrText,
  Hint,
  SectionTitle,
  dangerBtnStyle,
  inputStyle,
  smallBtnStyle,
} from '../station/stationTheme';
import type { CorpSummary } from './types';

interface Props {
  /** Corps the player can dissolve (manager only). */
  managedCorps: CorpSummary[];
  busy: boolean;
  error: string | null;
  onDissolve: (corpId: string) => void;
  onCancel: () => void;
}

/**
 * Dissolve a corporation. A notarial act — the manager must be at a station.
 * Server-side checks block dissolution while the corp still owns stations or
 * has an active manager-removal vote; bank balance is refunded to the manager.
 * The form requires the user to retype the corp tag to confirm intent.
 */
export function DissolveCorpForm({ managedCorps, busy, error, onDissolve, onCancel }: Props) {
  const [selectedId, setSelectedId] = useState<string>(managedCorps[0]?.id ?? '');
  const [confirmTag, setConfirmTag] = useState('');

  const selected = managedCorps.find((c) => c.id === selectedId);
  const tagMatches = selected ? confirmTag.trim().toUpperCase() === selected.tag.toUpperCase() : false;
  const canSubmit = !!selected && tagMatches && !busy;

  if (managedCorps.length === 0) {
    return (
      <>
        <Card>
          <SectionTitle>Dissolve a corporation</SectionTitle>
          <Hint>You don't manage any corporation, so there is nothing to dissolve here.</Hint>
        </Card>
        <button style={smallBtnStyle(false)} onClick={onCancel}>BACK</button>
      </>
    );
  }

  return (
    <>
      <Card>
        <SectionTitle>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <FiAlertTriangle size={12} style={{ color: PALETTE.RED }} />
            Dissolve a corporation
          </span>
        </SectionTitle>
        <Hint>
          This permanently deletes the corporation, kicks every member, cancels pending applications
          and asset-transfer offers, and refunds the bank balance to you. The corp must own no stations
          or planet structures and must not have an active manager-removal vote.
        </Hint>
      </Card>

      <Card>
        <CardRow>
          <CardLabel>Corporation</CardLabel>
          <CardValue>
            <select
              style={inputStyle}
              value={selectedId}
              onChange={(e) => { setSelectedId(e.target.value); setConfirmTag(''); }}
            >
              {managedCorps.map((c) => (
                <option key={c.id} value={c.id}>{c.name} [{c.tag}]</option>
              ))}
            </select>
          </CardValue>
        </CardRow>
        {selected && (
          <>
            <CardRow>
              <CardLabel>Type the tag to confirm</CardLabel>
              <CardValue>
                <input
                  style={{ ...inputStyle, width: 90 }}
                  value={confirmTag}
                  maxLength={4}
                  placeholder={selected.tag}
                  onChange={(e) => setConfirmTag(e.target.value.toUpperCase())}
                />
              </CardValue>
            </CardRow>
            <Hint style={{ color: tagMatches ? PALETTE.GREEN : PALETTE.DIM }}>
              {tagMatches ? `Confirmed. Click DISSOLVE to proceed.` : `Type "${selected.tag}" exactly to confirm.`}
            </Hint>
          </>
        )}
      </Card>

      <div style={{ display: 'flex', gap: 8 }}>
        <button style={smallBtnStyle(false)} onClick={onCancel} disabled={busy}>CANCEL</button>
        <button
          style={dangerBtnStyle(!canSubmit)}
          disabled={!canSubmit}
          onClick={() => selected && onDissolve(selected.id)}
        >
          {busy ? 'Dissolving…' : 'DISSOLVE CORPORATION'}
        </button>
      </div>

      {error && <ErrText>{error}</ErrText>}
    </>
  );
}
